import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { JSX } from 'react';
import type { DocumentFormat } from '@mem-ai-hub/shared';
import { generateDocument } from '@/services/documentApi';

interface DocumentDownloadProps {
  messageId: string;
  sessionId: string;
}

interface FormatOption {
  format: DocumentFormat;
  label: string;
  icon: JSX.Element;
}

const FORMAT_OPTIONS: FormatOption[] = [
  {
    format: 'docx',
    label: 'Word',
    icon: (
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  },
  {
    format: 'xlsx',
    label: 'Excel',
    icon: (
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    format: 'pptx',
    label: 'PowerPoint',
    icon: (
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    format: 'pdf',
    label: 'PDF',
    icon: (
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    format: 'md',
    label: 'Markdown',
    icon: (
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
      </svg>
    ),
  },
];

interface MenuPosition {
  top: number;
  left: number;
}

export default function DocumentDownload({
  messageId,
  sessionId,
}: DocumentDownloadProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState<DocumentFormat | null>(null);
  const [position, setPosition] = useState<MenuPosition>({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const MENU_HEIGHT = FORMAT_OPTIONS.length * 36 + 2; // approx item height + border
  const MENU_WIDTH = 176; // w-44 = 11rem = 176px

  /** Compute fixed position for the portal dropdown based on button rect. */
  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();

    // Default: menu opens above the button, right-aligned
    let top = rect.top - MENU_HEIGHT - 4;
    let left = rect.right - MENU_WIDTH;

    // If menu would go above viewport, flip below the button
    if (top < 4) {
      top = rect.bottom + 4;
    }

    // Clamp left edge to stay on-screen
    if (left < 4) {
      left = 4;
    }

    setPosition({ top, left });
  }, [MENU_HEIGHT]);

  // Close dropdown on outside click — check both button and menu refs
  useEffect(() => {
    if (!open) return;

    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (
        menuRef.current && !menuRef.current.contains(target) &&
        buttonRef.current && !buttonRef.current.contains(target)
      ) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Reposition on scroll (capture phase to catch ChatWindow's scrollable div) and resize
  useEffect(() => {
    if (!open) return;

    updatePosition();

    const onScrollOrResize = () => updatePosition();
    window.addEventListener('scroll', onScrollOrResize, true); // capture phase
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, updatePosition]);

  const handleMenuKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { setOpen(false); buttonRef.current?.focus(); }
  };

  async function handleGenerate(format: DocumentFormat) {
    setLoading(format);
    try {
      const result = await generateDocument(messageId, sessionId, format);
      // Open the SAS-signed download URL directly (no auth header needed)
      window.open(result.downloadUrl, '_blank');
    } catch (err) {
      console.error('Document generation failed:', err);
    } finally {
      setLoading(null);
      setOpen(false);
    }
  }

  const menu = open ? (
    <div
      ref={menuRef}
      className="w-44 rounded-md border border-border bg-bg shadow-lg"
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        zIndex: 50,
      }}
      role="menu"
      onKeyDown={handleMenuKeyDown}
    >
      {FORMAT_OPTIONS.map((opt) => (
        <button
          key={opt.format}
          type="button"
          role="menuitem"
          disabled={loading !== null}
          onClick={() => handleGenerate(opt.format)}
          className="flex w-full items-center gap-2 px-3 py-2 text-sm text-text-primary
            hover:bg-bg-secondary transition-colors first:rounded-t-md last:rounded-b-md
            disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading === opt.format ? (
            <svg
              className="h-4 w-4 animate-spin text-primary"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
          ) : (
            opt.icon
          )}
          <span>{opt.label}</span>
        </button>
      ))}
    </div>
  ) : null;

  return (
    <div className="relative inline-block">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen(!open)}
        className="inline-flex items-center justify-center rounded-md p-1.5 text-text-muted
          hover:bg-bg-tertiary hover:text-text-secondary transition-colors"
        title="Download as document"
        aria-label="Download as document"
        aria-haspopup="true"
        aria-expanded={open}
      >
        <svg
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      </button>

      {menu && createPortal(menu, document.body)}
    </div>
  );
}
